import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiCheckCircle, FiZap, FiTrendingUp, FiCalendar, FiClock } = FiIcons;

const CTASection = () => {
  const benefits = [
    {
      icon: FiCheckCircle,
      title: "Free Business Analysis",
      description: "We'll analyze your current setup and identify growth opportunities"
    },
    {
      icon: FiTarget,
      title: "Custom Growth Blueprint",
      description: "Get a personalized roadmap tailored to your MSME's specific needs"
    },
    {
      icon: FiZap,
      title: "Live Demo",
      description: "See exactly how FunnelsDone will transform your business operations"
    },
    {
      icon: FiTrendingUp,
      title: "ROI Projection",
      description: "Discover how much revenue you could generate in the next 90 days"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Book a <span className="text-yellow-300">FREE Strategy Call</span> & Get a Custom Growth Blueprint for Your Business
          </h2>
          <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            In just 30 minutes, discover exactly how to 3x your MSME revenue using AI funnels and WhatsApp automation
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <SafeIcon icon={benefit.icon} className="w-8 h-8 text-yellow-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
              <p className="text-gray-200 leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Main CTA */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 max-w-4xl mx-auto border border-white/20">
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              What You'll Get in This Call:
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8 text-left">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">Business Growth Assessment</h4>
                    <p className="text-gray-200 text-sm">Identify your biggest revenue blockers</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">Custom Funnel Strategy</h4>
                    <p className="text-gray-200 text-sm">Tailored plan for your industry & goals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">WhatsApp Automation Blueprint</h4>
                    <p className="text-gray-200 text-sm">Complete automation setup plan</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">Revenue Projection</h4>
                    <p className="text-gray-200 text-sm">See potential ROI within 90 days</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">Live Platform Demo</h4>
                    <p className="text-gray-200 text-sm">See FunnelsDone in action</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white">Implementation Roadmap</h4>
                    <p className="text-gray-200 text-sm">Step-by-step execution plan</p>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mb-6"
            >
              <a 
                href="https://funnelsdone.com/cash" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-12 py-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-2xl rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25"
              >
                👉 Book a Call With an Expert
                <SafeIcon icon={FiCalendar} className="ml-3 w-7 h-7" />
              </a>
            </motion.div>

            <div className="flex items-center justify-center space-x-6 text-sm text-gray-200">
              <div className="flex items-center">
                <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                <span>30-minute call</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-2 text-green-400" />
                <span>100% Free</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiTarget} className="w-4 h-4 mr-2" />
                <span>No obligations</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Urgency & Social Proof */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="bg-red-500/20 border border-red-400/30 rounded-2xl p-6 max-w-3xl mx-auto backdrop-blur-lg">
            <h4 className="text-xl font-bold text-white mb-2">⚠️ Limited Spots Available</h4>
            <p className="text-gray-200">
              We only take 20 strategy calls per week to ensure quality. 
              <strong className="text-yellow-300"> Book now to secure your spot!</strong>
            </p>
            
            <div className="flex items-center justify-center space-x-8 mt-4 text-sm">
              <div className="text-center">
                <div className="text-lg font-bold text-green-400">127</div>
                <div className="text-gray-300">Calls this month</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-yellow-400">98%</div>
                <div className="text-gray-300">Satisfaction rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-400">₹50L+</div>
                <div className="text-gray-300">Revenue generated</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;